import { type ComponentProps, memo, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';

import { type CreditUsageDisplayCategory } from '~/components/CreditUsageMeter/types';
import {
  CREDIT_USAGE_CATEGORY_COLOR_MAP,
  type CreditUsageDisplayCategoryUsage,
  formatCreditUsage,
  formatStorageUsage,
} from '~/components/CreditUsageMeter/utils';
import { useAnimatedNumber } from '~/hooks/useAnimatedNumber';

const LABEL_MAP = {
  generative: 'AI Actions',
  enrichedStorage: 'Enriched storage',
  storage: 'Storage',
} as const satisfies Record<CreditUsageDisplayCategory, string>;

export type CreditUsageBreakdownCategoryProps = Pick<ComponentProps<'div'>, 'className'> & {
  category: CreditUsageDisplayCategory;
} & CreditUsageDisplayCategoryUsage;

export const CreditUsageBreakdownCategory = memo(
  ({ category, className, ...usage }: CreditUsageBreakdownCategoryProps) => {
    const animatedCredits = useAnimatedNumber(usage.credits);
    const animatedGb = useAnimatedNumber('gb' in usage && usage.gb ? usage.gb : null);

    const usageString = useMemo(() => {
      const components: string[] = [];

      if (animatedGb) {
        components.push(formatStorageUsage(animatedGb));
      }

      components.push(`${formatCreditUsage(animatedCredits ?? usage.credits)} spent`);

      return components.join(' · ');
    }, [animatedCredits, animatedGb, usage.credits]);

    return (
      <div className="flex items-center justify-between gap-1 text-12">
        <div className="flex items-center gap-1">
          <span className={cn('size-1.5 rounded-full', CREDIT_USAGE_CATEGORY_COLOR_MAP[category])} />
          <span data-testid="CREDIT_USAGE_BREAKDOWN_CATEGORY_LABEL" className="truncate ads:text-metadata text-gray-9">
            {LABEL_MAP[category]}
          </span>
        </div>

        <span data-testid="CREDIT_USAGE_BREAKDOWN_CATEGORY_USAGE" className="truncate">
          {usageString}
        </span>
      </div>
    );
  },
);

CreditUsageBreakdownCategory.displayName = 'CreditUsageBreakdownCategory';
